import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Bed,
  Star,
  Phone,
  Mail,
  Calendar,
  Clock,
  ArrowLeft,
  CheckCircle,
} from "lucide-react";
import FacilityImageCarousel from "./FacilityImageCarousel";
import EmbeddedMap from "./EmbeddedMap";
import { facilitiesData } from "../data/facilities";

const FacilityDetailPage: React.FC = () => {
  const { facilityId } = useParams<{ facilityId: string }>();

  // const facility = facilitiesData[facilityId as keyof typeof facilitiesData];
  const facility = facilitiesData.find((f) => f.id === facilityId);

  console.log({ facility }, { facilityId });
  if (!facility) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Facility Not Found
          </h1>
          <Link
            to="/facilities"
            className="text-primary-green hover:text-blue-800"
          >
            Return to Facilities
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-8">
      {/* Header */}
      <div className="container mx-auto px-4 py-0">
        <Link
          to="/facilities"
          className="inline-flex items-center text-primary-green hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to All Facilities
        </Link>
      </div>

      {/* Hero Section */}
      {/* <section className="relative">
        <div className="relative h-96 bg-gray-900">
          <img
            src={facility.images[0]}
            alt={facility.name}
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-5xl font-bold mb-4">{facility.name}</h1>
              <div className="flex items-center justify-center space-x-6 text-lg">
                <div className="flex items-center">
                  <a
                    className="flex items-center mb-2"
                    href={`${facility.mapLink}`}
                    target="_blank"
                  >
                    <MapPin className="h-5 w-5 mr-2" />
                    {facility.location}
                  </a>
                </div>
                <div className="bg-primary-green px-3 py-1 rounded-full text-sm font-semibold">
                  {facility.specialty}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Hero Section with Carousel */}
      <section className="relative">
        <div className="container mx-auto px-4 py-0">
          <div className="mt-8 text-center">
            <h1 className="text-5xl font-bold mb-4 text-gray-900">
              {facility.name}
            </h1>
            <div className="flex items-center justify-center space-x-6 text-lg">
              <div className="flex items-center">
                <a
                  className="flex items-center mb-2 text-gray-700 hover:text-blue-600"
                  href={`${facility.mapLink}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapPin className="h-5 w-5 mr-2" />
                  {facility.location}
                </a>
              </div>
              <div className="bg-primary-green px-3 py-1 rounded-full text-sm font-semibold text-white">
                {facility.specialty}
              </div>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <FacilityImageCarousel
              images={facility.images}
              facilityName={facility.name}
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  About {facility.name}
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  {facility.description}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {facility.detailedDescription}
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Amenities & Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {facility.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Services Provided
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {facility.services.map((service, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{service}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Our Care Team
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {facility.staff}
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-8 sticky top-8">
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <Bed className="h-5 w-5 text-gray-600 mr-2" />
                    <span className="text-gray-700">
                      {facility.beds} beds available
                    </span>
                  </div>
                  <div className="flex items-center mb-6">
                    <Star className="h-5 w-5 text-yellow-400 fill-current mr-2" />
                    <span className="text-gray-700">5.0 rating</span>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Contact Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-primary-green mr-3" />
                      <span className="text-gray-700">{facility.phone}</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-primary-green mr-3" />
                      <span className="text-gray-700 text-sm">
                        {facility.email}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Location
                  </h4>
                  <EmbeddedMap
                    location={facility.location}
                    mapLink={facility.mapLink}
                    height={250}
                    className="mb-4"
                  />
                  <div className="text-center">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                        facility.location
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary-green hover:text-blue-700 text-sm font-medium"
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                        />
                      </svg>
                      Get Directions
                    </a>
                  </div>
                </div>

                <div className="space-y-4">
                  <button className="w-full bg-primary-green text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Schedule a Tour
                  </button>
                  <button className="w-full border-2 border-blue-600 text-primary-green py-3 px-6 rounded-lg font-semibold hover:bg-primary-green hover:text-white transition-colors duration-300 flex items-center justify-center">
                    <Clock className="h-5 w-5 mr-2" />
                    Check Availability
                  </button>
                </div>

                <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Need More Information?
                  </h4>
                  <p className="text-gray-700 text-sm mb-4">
                    Our care coordinators are available to answer questions and
                    help you find the perfect fit.
                  </p>
                  <Link
                    to="/contact#contact"
                    className="text-primary-green hover:text-blue-800 font-medium text-sm"
                  >
                    Contact our team →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FacilityDetailPage;
